import { useRef, useEffect } from 'react'
/**
 * useDebounce hook
 * @param {function} fn - function that will be executed after timeout milliseconds, unless it is invoked again
 * @param {number} timeout - delay in milliseconds to execute the fn
 * @param {boolean} cleanUpOnUnmount - allows to clean the timeout when the component is unmounted
 * @returns {function} - function that will execute fn after delay, or refresh execution delay if is in delay timeframe
 */
export default function useDebounce(fn, timeout, cleanUpOnUnmount = false) {
  const debounce = useRef(null)
  // more info on the use of useRef here: https://stackoverflow.com/questions/53090432/react-hooks-right-way-to-clear-timeouts-and-intervals

  // necessary to clear memory leak if the component is unmounted before the execution of this debounce
  useEffect(() => {
    if (cleanUpOnUnmount) {
      return () => clearTimeout(debounce.current)
    }
  }, [])

  const invoke = (...args) => {
    // if it's about to run, then clear the timeout so it's refreshed
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => fn(...args), timeout)
  }

  return invoke
}
